import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Typography, Box, Tab, DialogContent, Grid } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { selectPaymentState, clearPayModalReduxStates, makeOneTimePayment, selectBalanceDetails, getPaymentFeesByResident, getReplaceAutoPay, getCreateRecurringPayment, getTransactionHistory, selectPaymentMethods } from '../sagas/residentpaymentsSlice';
import OtpModalContents from './OtpModalContents';
import { AmountTier, AutoPayType, BalanceDetailsDTO, BankCardDTOCardTypeEnum, BankCardDTOProcessAsCardTypeEnum, CreateOneTimePaymentDto, CreateScheduledAutoPayDto, FlatAutopayFeeDto, ResidentDTO } from '@inosio/payments-sdk';
import ReviewAndSubmitModal from '../../../shared/Modal/ReviewAndSubmitModal';
import { UIContextEnum, defaultUIContext } from '../../../pmui/residents/MakeAdhocOtp';
import PaymentReviewModal from '../../../shared/Modal/PaymentReviewModal';
import { format, getDate } from 'date-fns';
import { getCreateAdhocOTP, getResidentPaymentFees, selectResidentBalanceDetails, selectResidents } from '../../../pmui/residents/saga/pmResidentsSlice';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import ThankYouForYourPayment from '../../../shared/Modal/ThankYouForYourPayment';
import RecurringModalContents, { RecurringModalContentsReturnType } from './RecurringModalContents';
import PaymentBalanceDetails from './PaymentBalanceDetails';
import { MaskedInputState } from '../../../shared/MaskedInput';
import { useRecentOTPsAndAutoPays, useSelectedPptId, useIsPmAdhoc, getSumOfPropPayTypesAmount, getFrequency, minorUnitsToFixedString, isActionAllowedInRange } from './UseSharedMethods';
import RecurringPayMethodsModal, { CreateApDto } from './RecurringPayMethodsModal';
import RecurringThankYouModal from '../../../shared/Modal/RecurringThankYouModal';
import { convertSqlDateToUSCalendarDate } from '../../../shared/util/converters';
import DebitCardNonDiscountModal from '../../../shared/Modal/DebitCardNonDiscountModal';
import Cookies from 'js-cookie';

const useStyles = makeStyles({
  paymentModalContainer: {
    boxShadow: 'none',
    borderRadius: '24px',
  },
  wrapper: {
    margin: '1px',
    position: 'relative',
  },
  buttonProgress: {
    color: '#053742',
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  stepContainer: {
    width: '50% !important',
    margin: '0 auto !important',
    textTransform: 'capitalize',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: ' 18px !important',
    lineHeight: '18px !important',
  },
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '30px',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '340px',
    height: '49px',
    justifyContent: 'center',
  },
});

export enum ResPaymentType {
  OTP = 'OTP',
  Recurring = 'Recurring',
}

interface MakePaymentModalProps {
  open: boolean;
  residentsDetails: ResidentDTO;
  currentTab?: ResPaymentType;
  isEditingAutoPay?: boolean;
  autopayUuid?: string;
  openBalanceDetailsModal?: boolean;
  closeCallback: () => void;
}

export default function MakePaymentModal(props: MakePaymentModalProps) {
  const currentUI = React.useContext(defaultUIContext);
  const isPmAdhoc = useIsPmAdhoc();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const { otps: recentOtps, autopays: approachingAutopays } = useRecentOTPsAndAutoPays(currentUI);
  const { paymentFeesError, paymentConfigs, AutoPays, isMakeOtpError, isMakeOtpSuccess, makeOneTimePaymentError, PaymentMadeDetails, isRecurringSuccess, recurringPaymentError, isReplaceAutoPaySuccess, replaceAutoPayError, RawPaymentFeesByResident } = useSelector(selectPaymentState);
  const showRecurringTab = !isPmAdhoc && !!paymentConfigs?.AutoPayConfigs?.find((ap) => ap.Enabled);
  const { isAdhocOTPSuccess, isAdhocOTPError, adhocOTPError, adhocOTPResponse } = useSelector(selectResidents);
  const residentsDetails = props.residentsDetails;
  const [openBalanceDetailsModal, setOpenBalanceDetailsModal] = React.useState(!!props.openBalanceDetailsModal);
  const [recurringPaymentStartDayNumber, setRecurringPaymentStartDayNumber] = React.useState(0);
  const [globalError, setGlobalError] = React.useState<string | null>(null);
  const currentBalanceInfo = useSelector(isPmAdhoc ? selectResidentBalanceDetails : selectBalanceDetails);
  const selectedPptId = useSelectedPptId();

  const initialMaskedInputValues = {
    amountOwed: minorUnitsToFixedString(currentBalanceInfo.PropertyPaymentTypes?.[0]?.Amount ?? 0),
    misc: minorUnitsToFixedString(currentBalanceInfo.PropertyPaymentTypes?.[1]?.Amount ?? 0),
    securityDeposit: minorUnitsToFixedString(currentBalanceInfo.PropertyPaymentTypes?.[2]?.Amount ?? 0),
  };
  const [contextBalanceInfo, setContextBalanceInfo] = React.useState<BalanceDetailsDTO>(currentBalanceInfo); //this will toggle based on which tab is selected (otp or autopay)
  const [maskedInputValues, setMaskedInputValues] = React.useState<MaskedInputState>(initialMaskedInputValues);
  const [currentTab, setCurrentTab] = React.useState<ResPaymentType>(props.currentTab ?? ResPaymentType.OTP);
  const handleChangeTabValue = (event: React.SyntheticEvent, newValue: ResPaymentType) => {
    setCurrentTab(newValue);
    setMaskedInputValues(initialMaskedInputValues);
    setRecurringModalValue({} as RecurringModalContentsReturnType);
  };

  //helper method to update the payment rules depending on OTP or AP. Since the balanceInfo box is shared between OTP and AP, we can leverage the dependency of the balance obj
  const buildSetContextBalanceInfo = (resPaymentType: ResPaymentType) => {
    let updatedBalanceInfoContext: BalanceDetailsDTO = JSON.parse(JSON.stringify(currentBalanceInfo));
    const defaultToTodayNum = new Date().getDate(); //for the first go when they open the modal.

    if (resPaymentType === ResPaymentType.Recurring) {
      const prePayConfig = paymentConfigs?.PaymentRestrictions?.find((pr) => pr.Type === 'PrePaymentAllowed');
      const partialPayConfig = paymentConfigs?.PaymentRestrictions?.find((pr) => pr.Type === 'PartialPaymentAllowed');
      const overPayConfig = paymentConfigs?.PaymentRestrictions?.find((pr) => pr.Type === 'OverPaymentAllowed');
      //compute min/max depending on configs
      const isPrePaymentAllowedOnSelectedDay = isActionAllowedInRange(prePayConfig?.NotBeforeDay ?? 0, prePayConfig?.NotAfterDay ?? 32, recurringPaymentStartDayNumber || defaultToTodayNum);
      const isPartialPaymentAllowedOnSelectedDay = residentsDetails.Lease.AllowPartialPayments === null ? isActionAllowedInRange(partialPayConfig?.NotBeforeDay ?? 0, partialPayConfig?.NotAfterDay ?? 32, recurringPaymentStartDayNumber || defaultToTodayNum) : residentsDetails.Lease.AllowPartialPayments;
      const isOverPaymentAllowedOnSelectedDay = isActionAllowedInRange(overPayConfig?.NotBeforeDay ?? 0, overPayConfig?.NotAfterDay ?? 32, recurringPaymentStartDayNumber || defaultToTodayNum);

      //since this is autopay and we may not have the latest balance info that will be avail on pmt day, this logic may need to be looked at
      if (updatedBalanceInfoContext.Balance > 0) {
        //non-zero balance logic, ignore pre-payment logic and take into account partial payment b/c we assume a non-0 balance means it's a partial pay
        if (isPartialPaymentAllowedOnSelectedDay) {
          updatedBalanceInfoContext.MinPayment = 0;
        } else updatedBalanceInfoContext.MinPayment = updatedBalanceInfoContext.Balance;

        if (isOverPaymentAllowedOnSelectedDay) {
          updatedBalanceInfoContext.MaxPayment = paymentConfigs.SinglePaymentMaxLimit;
        } else updatedBalanceInfoContext.MaxPayment = updatedBalanceInfoContext.Balance;
      } else {
        //zero balance logic, take into account pre-payment logic, and ignore partial payment (reason is we assume bc of 0 balance, that it's a prepay)
        updatedBalanceInfoContext.MinPayment = 0; //there is no minimum, since we have no way to know b/c their balance is 0.
        if (isPrePaymentAllowedOnSelectedDay) {
          updatedBalanceInfoContext.MaxPayment = paymentConfigs.SinglePaymentMaxLimit;
        } else {
          updatedBalanceInfoContext.MaxPayment = 0; //max is zero. ie, they can't make a payment on this date b/c there is no balance and prepay is not allowed.
        }
      }
    }
    //missing else block means: must be otp tab, no need to change currentBalanceInfoContext in that case.

    setContextBalanceInfo(updatedBalanceInfoContext);
  };

  React.useEffect(() => {
    buildSetContextBalanceInfo(currentTab);
  }, [recurringPaymentStartDayNumber, currentTab, currentBalanceInfo]);

  React.useEffect(() => {
    //This is for the Heads Up modal(duplication pay notification modal). For some reason we don't get the refreshed payment history.
    dispatch(getTransactionHistory());

    dispatch({
      type: clearPayModalReduxStates.type,
    });
  }, []);

  React.useEffect(() => {
    const amountOwedPpt = currentBalanceInfo.PropertyPaymentTypes?.find((ppt) => ppt.Label === 'Amount Owed');
    const miscPpt = currentBalanceInfo.PropertyPaymentTypes?.find((ppt) => ppt.Label === 'Misc');
    const secDepPpt = currentBalanceInfo.PropertyPaymentTypes?.find((ppt) => ppt.Label === 'Security Deposit');

    setMaskedInputValues({
      amountOwed: minorUnitsToFixedString(amountOwedPpt?.Amount ?? 0),
      misc: minorUnitsToFixedString(miscPpt?.Amount ?? 0),
      securityDeposit: minorUnitsToFixedString(secDepPpt?.Amount ?? 0),
    });
  }, [currentBalanceInfo]);

  const handleMakePaymentModalClose = () => {
    props.closeCallback();
    setCreateOtpDto({} as CreateOneTimePaymentDto);
    setLastFour('');
    setGlobalError(null);
    dispatch({
      type: clearPayModalReduxStates.type,
    });
  };

  //start: OTP flow
  const [openReviewOtpModal, setOpenReviewOtpModal] = React.useState(false);
  const [openUnRegDbFeeModal, setOpenUnRegDbFeeModal] = React.useState(false);
  const [createOtpDto, setCreateOtpDto] = React.useState<CreateOneTimePaymentDto>({} as CreateOneTimePaymentDto);
  const [feesCents, setFeesCents] = React.useState(0);
  const [lastFour, setLastFour] = React.useState('');
  const [selectedTiers, setSelectedTiers] = React.useState<AmountTier[]>([]);
  const savedPaymentMethods = useSelector(selectPaymentMethods);
  const [dbCardUnregCookieKey, setDbCardUnregCookieKey] = React.useState('');
  const handleOtpContinue = (returnObj: { createOtpDto: CreateOneTimePaymentDto; lastFour: string }) => {
    //Need to check if 1) it's an unreg db and 2) they haven't seen the page for this db yet then they get the warning page.
    const thisPayMethod = savedPaymentMethods.BankCards.find((bc) => bc.Uuid === returnObj.createOtpDto.FeePayMethodUuid);
    if (!!thisPayMethod && thisPayMethod.CardType === BankCardDTOCardTypeEnum.Db && thisPayMethod.ProcessAsCardType === BankCardDTOProcessAsCardTypeEnum.Cc) {
      const cookieKey = `db_unreg_fee_ack_${thisPayMethod?.Uuid}`;
      setDbCardUnregCookieKey(cookieKey);
      const previousDbFeeAckCookie = Cookies.get(cookieKey);
      //unregulated or prepaid debit
      if (!!previousDbFeeAckCookie) {
        //payer already acknowledged a fee for this card.
        setOpenReviewOtpModal(true);
      } else {
        setOpenUnRegDbFeeModal(true);
      }
    } else {
      setOpenReviewOtpModal(true);
    }

    setCreateOtpDto(returnObj.createOtpDto);
    setLastFour(returnObj.lastFour);
  };

  const [openHeadsUpModal, setOpenHeadsUpModal] = React.useState(false);
  const handleReviewOtpConfirm = () => {
    // skip payment review if likely aren't any duplicates
    if (!recentOtps.length && !approachingAutopays.length) {
      handleOtpPaymentSubmit(); // submit payment
      return;
    } else {
      setCreateOtpDto({ ...createOtpDto, Memo: 'OTP - Possible Duplicate Acknowledged' });
      setOpenHeadsUpModal(true);
    }
  };

  const handleOtpPaymentSubmit = () => {
    setOpenReviewOtpModal(false);
    setOpenHeadsUpModal(false);
    setIsSpinnerModalOpen(true);

    if (currentUI !== UIContextEnum.pmUI) {
      dispatch(makeOneTimePayment(createOtpDto));
    } else {
      dispatch({ type: getCreateAdhocOTP.type, payload: createOtpDto });
    }
  };

  React.useEffect(() => {
    if (isMakeOtpSuccess || isAdhocOTPSuccess) {
      setSpinnerStatus('stop');
      setGlobalError(null);
    }

    if (isMakeOtpError || isAdhocOTPError) {
      handleCloseSpinnerModal();
      setGlobalError(isPmAdhoc ? adhocOTPError : makeOneTimePaymentError);
      setOpenReviewOtpModal(true);
    }
  }, [isMakeOtpSuccess, isAdhocOTPSuccess, isMakeOtpError, makeOneTimePaymentError, isAdhocOTPError, adhocOTPError]);
  //end: OTP flow

  //start: Recurring Payment flow
  const [recurringModalValue, setRecurringModalValue] = React.useState<RecurringModalContentsReturnType>({} as RecurringModalContentsReturnType);
  const [openRecurringPayMethodsModal, setOpenRecurringPayMethodsModal] = React.useState(false);
  const handleRecurringContinue = (returnObj: RecurringModalContentsReturnType) => {
    setRecurringModalValue(returnObj);
    setOpenRecurringPayMethodsModal(true);
  };

  const [createApDto, setCreateApDto] = React.useState<CreateApDto>({} as CreateApDto);
  const handleRecurringPaymentContinue = (returnObj: { createApDto: CreateApDto; feesCents: number; lastFour: string; tiers?: AmountTier[] }) => {
    setOpenRecurringPayMethodsModal(false);
    setFeesCents(returnObj.feesCents);
    setCreateApDto(returnObj.createApDto);
    setLastFour(returnObj.lastFour);
    returnObj.tiers && setSelectedTiers(returnObj.tiers);
    setOpenReviewApModal(true);
  };

  const [openReviewApModal, setOpenReviewApModal] = React.useState(false);

  const handleReviewApConfirm = () => {
    setOpenReviewApModal(false);
    setIsSpinnerModalOpen(true);

    let payloadToSubmit = createApDto;
    if (createApDto.VariableMaxAmountAllowed === 0) delete payloadToSubmit.VariableMaxAmountAllowed;

    if ((createApDto.FeeSchedule as FlatAutopayFeeDto).Amount === 0) {
      const rawFeeDto = createApDto.RawFeeDto;
      const rawPaymentFeeDto = RawPaymentFeesByResident.find((raw) => raw.PaidBy === rawFeeDto?.PaidBy && raw.PaymentType === rawFeeDto?.PaymentType);
      payloadToSubmit.FeeSchedule = { Amount: rawPaymentFeeDto?.FeeAmount, PaidBy: rawPaymentFeeDto?.PaidBy } as FlatAutopayFeeDto;
    }

    delete payloadToSubmit.RawFeeDto;

    dispatch({
      type: props?.isEditingAutoPay ? getReplaceAutoPay.type : getCreateRecurringPayment.type,
      payload: payloadToSubmit as CreateScheduledAutoPayDto,
      autopayUid: props?.autopayUuid,
    });
  };

  React.useEffect(() => {
    if (isRecurringSuccess || isReplaceAutoPaySuccess) {
      setSpinnerStatus('stop');
      setGlobalError(null);
    }
    if (recurringPaymentError?.length !== 0 || replaceAutoPayError?.length !== 0) {
      setGlobalError(recurringPaymentError || replaceAutoPayError);
      handleCloseSpinnerModal();
      setOpenReviewApModal(true);
    }
  }, [isRecurringSuccess, isReplaceAutoPaySuccess, recurringPaymentError, replaceAutoPayError]);

  const promoteToAP = () => {
    setCreateOtpDto({} as CreateOneTimePaymentDto);
    setLastFour('');
    setGlobalError(null);
    dispatch({
      type: clearPayModalReduxStates.type,
    });
    setMaskedInputValues(initialMaskedInputValues);
    setCurrentTab(ResPaymentType.Recurring);
  };
  //end: Recurring Payment flow

  React.useEffect(() => {
    // NB:
    // There are two fees endpoint (pm and res) and five (5) scenarios where we make a fees call
    // (1) res in resUI
    // (2) pm in pmUI
    // (3) pm in resUI
    // (4) inosioUser in resUI
    // (5) inosio User in pm UI
    // all 5 consume this payment dialog component but the only time we ever hit the pm endpoint for fees
    // is when the pm (actual or impersonated by inosioUser) is in the pmUI
    let paymentAmountCents = getSumOfPropPayTypesAmount(maskedInputValues);
    if (recurringModalValue.Type === AutoPayType.Variable) paymentAmountCents = 0;

    if (isPmAdhoc) {
      dispatch({
        // for pm adhoc otp
        type: getResidentPaymentFees.type,
        payload: {
          residentUuid: residentsDetails?.Uuid,
          paymentAmount: paymentAmountCents,
          propertyPaymentTypeId: selectedPptId | 0,
        },
      });
      return;
    }

    if (selectedPptId) {
      dispatch({
        type: getPaymentFeesByResident.type,
        payload: {
          moneyToSubmit: paymentAmountCents,
          propPayTypeId: selectedPptId,
        },
      });
    }
  }, [selectedPptId, maskedInputValues, recurringModalValue]);

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const handleCloseSpinnerModal = () => {
    setIsSpinnerModalOpen(false);
    setSpinnerStatus(null);
  };

  return (
    <div id="payment-modal" className={classes.paymentModalContainer}>
      <Dialog
        fullScreen={isOnMobileScreen}
        open={props.open}
        onClose={handleMakePaymentModalClose}
        fullWidth={true}
        disableEnforceFocus
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            height: !isOnMobileScreen ? '700px' : '100%',
            borderRadius: !isOnMobileScreen ? '24px' : undefined,
            boxSizing: 'border-box',
          },
        }}
      >
        <DialogContent>
          <TabContext value={currentTab}>
            <Box
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'capitalize',
                },
              }}
            >
              {paymentFeesError && (
                <Grid container sx={{ width: '100%', display: 'flex', justifyContent: 'center' }} mb={2}>
                  <Grid item className={classes.globalError}>
                    <Typography
                      sx={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                      }}
                    >
                      {paymentFeesError}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              <TabList onChange={handleChangeTabValue} TabIndicatorProps={{ style: { color: 'red' } }} aria-label="lab API tabs example" id="payMethodTabList">
                <Tab label="Now" value={ResPaymentType.OTP} className={classes.stepContainer} />
                {showRecurringTab ? <Tab label="Recurring" value={ResPaymentType.Recurring} className={classes.stepContainer} disabled={AutoPays && AutoPays.length > 0} /> : ''}
              </TabList>
            </Box>

            <TabPanel value={ResPaymentType.OTP} sx={{ pl: 0, pr: 0 }}>
              {currentTab === ResPaymentType.OTP && <OtpModalContents residentsDetails={residentsDetails} maskedInputValues={maskedInputValues} setOpenBalanceDetailsModal={setOpenBalanceDetailsModal} closeCallback={handleMakePaymentModalClose} continueCallback={handleOtpContinue} />}
            </TabPanel>

            {showRecurringTab && (
              <TabPanel value={ResPaymentType.Recurring} sx={{ pl: 0, pr: 0 }}>
                {currentTab === ResPaymentType.Recurring && <RecurringModalContents maskedInputValues={maskedInputValues} setOpenBalanceDetailsModal={setOpenBalanceDetailsModal} isEditingMode={!!props.isEditingAutoPay} closeCallback={handleMakePaymentModalClose} setRecurrintStartDayNum={setRecurringPaymentStartDayNumber} continueCallback={handleRecurringContinue} />}
              </TabPanel>
            )}
          </TabContext>
        </DialogContent>
      </Dialog>

      {openBalanceDetailsModal && (
        <PaymentBalanceDetails
          balanceInfoContext={contextBalanceInfo}
          maskedInputValues={maskedInputValues}
          open={openBalanceDetailsModal}
          closeCallback={() => setOpenBalanceDetailsModal(false)}
          continueCallback={(value: MaskedInputState) => {
            setOpenBalanceDetailsModal(false);
            setMaskedInputValues(value);
          }}
        />
      )}

      {openUnRegDbFeeModal && (
        <DebitCardNonDiscountModal
          allFees={[]}
          targetDate={getDate(new Date())}
          irpPaymentClass="OTP"
          thisDbFee={createOtpDto.FeeAmount || 0}
          open={openUnRegDbFeeModal}
          proceedWithFee={() => {
            Cookies.set(dbCardUnregCookieKey, new Date().getTime().toString(), { expires: 365 });
            setOpenReviewOtpModal(true); //open review modal
            setOpenUnRegDbFeeModal(false); //close this modal
          }}
          onClose={() => {
            setOpenUnRegDbFeeModal(false); //close this modal
          }}
          back={() => {
            setGlobalError(null);
            setOpenUnRegDbFeeModal(false); //close this modal
          }}
        />
      )}

      {openReviewOtpModal && (
        <ReviewAndSubmitModal
          open={openReviewOtpModal}
          propertyName={residentsDetails && residentsDetails.Lease?.PropertyName}
          pmCompany={residentsDetails && residentsDetails.Lease?.CompanyName}
          name={residentsDetails && `${residentsDetails.FirstName || ''} ${residentsDetails.MiddleName || ''} ${residentsDetails.LastName || ''}`}
          paidAmount={(createOtpDto.TotalAmount - (createOtpDto.FeeAmount ?? 0)) / 100}
          totalAmount={createOtpDto.TotalAmount / 100}
          transactionDate={format(new Date(), 'MM/dd/yyyy')}
          paymentFees={(createOtpDto.FeeAmount ?? 0) / 100}
          last4={lastFour}
          displayHeader={false}
          globalError={globalError}
          closeCallback={() => setOpenReviewOtpModal(false)}
          backCallback={() => {
            setGlobalError(null);
            setOpenReviewOtpModal(false);
          }}
          cancelCallback={handleMakePaymentModalClose}
          confirmCallback={handleReviewOtpConfirm}
        />
      )}

      {openHeadsUpModal && (
        <PaymentReviewModal
          open={openHeadsUpModal}
          onClose={() => setOpenHeadsUpModal(false)}
          submitPayment={handleOtpPaymentSubmit}
          back={() => {
            setOpenReviewOtpModal(false);
            setOpenHeadsUpModal(false);
          }}
          recentOTPs={recentOtps}
          recentAutoPays={approachingAutopays}
        />
      )}

      {(isMakeOtpSuccess || isAdhocOTPSuccess) && (
        <ThankYouForYourPayment
          openStateThankYou={isMakeOtpSuccess || isAdhocOTPSuccess}
          closeCallbackThankYou={handleMakePaymentModalClose}
          totalAmount={createOtpDto.TotalAmount / 100}
          paymentID={(isPmAdhoc ? adhocOTPResponse : PaymentMadeDetails)?.TransactionUuid}
          transactionDate={(isPmAdhoc ? adhocOTPResponse : PaymentMadeDetails)?.TransactionDate ?? ''}
          paidAmount={(createOtpDto.TotalAmount - (createOtpDto.FeeAmount ?? 0)) / 100}
          fee={(createOtpDto.FeeAmount ?? 0) / 100}
          pmCompany={(residentsDetails && residentsDetails?.Lease?.CompanyName) ?? ''}
          propertyName={residentsDetails && residentsDetails?.Lease?.PropertyName}
          leaseHolderName={residentsDetails && `${residentsDetails?.FirstName || ''} ${residentsDetails?.MiddleName || ''} ${residentsDetails?.LastName || ''}`}
          last4={lastFour}
          deviceWidth={window.innerWidth}
          promoteToAP={promoteToAP}
        />
      )}

      {openRecurringPayMethodsModal && (
        <RecurringPayMethodsModal
          open={openRecurringPayMethodsModal}
          maskedInputValues={maskedInputValues}
          savedAutoPayValues={recurringModalValue}
          closeCallback={() => setOpenRecurringPayMethodsModal(false)}
          backCallback={() => {
            // setGlobalError(null);

            setOpenRecurringPayMethodsModal(false);
          }}
          cancelCallback={handleMakePaymentModalClose}
          continueCallback={handleRecurringPaymentContinue}
        />
      )}

      {openReviewApModal && (
        <ReviewAndSubmitModal
          open={openReviewApModal}
          propertyName={residentsDetails && residentsDetails.Lease.PropertyName}
          pmCompany={residentsDetails && residentsDetails.Lease?.CompanyName}
          name={residentsDetails && `${residentsDetails.FirstName || ''} ${residentsDetails.MiddleName || ''} ${residentsDetails.LastName || ''}`}
          paidAmount={createApDto.Type === AutoPayType.Variable ? (createApDto.VariableMaxAmountAllowed ?? 0) / 100 : (createApDto.FixedAmount ?? 0) / 100}
          totalAmount={createApDto.Type === AutoPayType.Variable ? ((createApDto.VariableMaxAmountAllowed ?? 0) + feesCents) / 100 : ((createApDto.FixedAmount ?? 0) + feesCents) / 100}
          transactionDate={format(new Date(), 'MM/dd/yyyy')}
          paymentFees={feesCents / 100}
          last4={lastFour}
          selectedPaymentMethodTiers={selectedTiers}
          displayHeader={true}
          type={createApDto.Type === AutoPayType.Variable ? 'full' : 'fixed'}
          displayText={
            createApDto.Type === AutoPayType.Fixed
              ? `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(createApDto.StartDate)} and every ${getFrequency(createApDto.Frequency)} thereafter for $${(((createApDto.FixedAmount ?? 0) + feesCents) / 100).toFixed(2)} 
              ${!createApDto.EndDate ? 'until you cancel it' : `with the final payment on ${convertSqlDateToUSCalendarDate(createApDto.EndDate ?? '')}`}`
              : `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(createApDto.StartDate)} and every ${getFrequency(createApDto.Frequency)} thereafter for your full balance on file 
                ${!createApDto.VariableMaxAmountAllowed ? '' : ` up to $${((createApDto.VariableMaxAmountAllowed ?? 0) / 100).toFixed(2)}`}  + fee ${!createApDto.EndDate ? 'until you cancel it' : `with the final payment on ${convertSqlDateToUSCalendarDate(createApDto.EndDate ?? '')}`}`
          }
          globalError={globalError}
          closeCallback={() => setOpenReviewApModal(false)}
          backCallback={() => {
            setGlobalError(null);
            setOpenReviewApModal(false);
          }}
          cancelCallback={handleMakePaymentModalClose}
          confirmCallback={handleReviewApConfirm}
        />
      )}

      {(isRecurringSuccess || isReplaceAutoPaySuccess) && (
        <RecurringThankYouModal
          openStateThankYou={isRecurringSuccess || isReplaceAutoPaySuccess}
          closeCallbackThankYou={handleMakePaymentModalClose}
          totalAmount={createApDto.Type === AutoPayType.Variable ? ((createApDto.VariableMaxAmountAllowed ?? 0) + feesCents) / 100 : ((createApDto.FixedAmount ?? 0) + feesCents) / 100}
          transactionDate={format(new Date(), 'MM/dd/yyyy')}
          paidAmount={createApDto.Type === AutoPayType.Variable ? (createApDto.VariableMaxAmountAllowed ?? 0) / 100 : (createApDto.FixedAmount ?? 0) / 100}
          paymentFees={feesCents / 100}
          pmCompany={(residentsDetails && residentsDetails?.Lease?.CompanyName) ?? ''}
          propertyName={residentsDetails && residentsDetails?.Lease?.PropertyName}
          leaseHolderName={residentsDetails && `${residentsDetails?.FirstName || ''} ${residentsDetails?.MiddleName || ''} ${residentsDetails?.LastName || ''}`}
          last4={lastFour}
          selectedPaymentMethodTiers={selectedTiers}
          transactionDateShort={format(new Date(), 'MM/dd/yyyy')}
          type={createApDto.Type === AutoPayType.Variable ? 'full' : 'fixed'}
          header={
            createApDto.Type === AutoPayType.Fixed
              ? `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(createApDto.StartDate)} and every ${getFrequency(createApDto.Frequency)} thereafter for $${(((createApDto.FixedAmount ?? 0) + feesCents) / 100).toFixed(2)} 
              ${!createApDto.EndDate ? 'until you cancel it' : `with the final payment on ${convertSqlDateToUSCalendarDate(createApDto.EndDate ?? '')}`}`
              : `Your recurring payment will process on ${convertSqlDateToUSCalendarDate(createApDto.StartDate)} and every ${getFrequency(createApDto.Frequency)} thereafter for your full balance on file 
                ${!createApDto.VariableMaxAmountAllowed ? '' : ` up to $${((createApDto.VariableMaxAmountAllowed ?? 0) / 100).toFixed(2)}`}  + fee ${!createApDto.EndDate ? 'until you cancel it' : `with the final payment on ${convertSqlDateToUSCalendarDate(createApDto.EndDate ?? '')}`}`
          }
        />
      )}

      {isSpinnerModalOpen && <SpinnerModal isInvisible={true} height={isOnMobileScreen ? '100%' : '700px'} openSpinnerModal={isSpinnerModalOpen} handleCloseSpinnerModal={handleCloseSpinnerModal} spinnerStatus={spinnerStatus} closeCancel={handleCloseSpinnerModal} />}
    </div>
  );
}
